import api from './api';

const slotService = {
    getSlots: async () => {
        try {
            const response = await api.get('/slots');
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    getAvailableSlots: async () => {
        try {
            const response = await api.get('/slots/available');
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    createSlot: async (slotData) => {
        try {
            if (!slotData || !slotData.slotDateTime) {
                throw new Error('Slot date and time are required');
            }

            const response = await api.post('/slots', slotData);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Create multiple slots at once
    createBulkSlots: async (slotsData) => {
        try {
            if (!Array.isArray(slotsData) || slotsData.length === 0) {
                throw new Error('Slots data array is required');
            }

            // Validate all slots before sending
            slotsData.forEach((slot, index) => {
                if (!slot.slotDateTime) {
                    throw new Error(`Slot ${index + 1}: Date and time are required`);
                }
                if (!slot.interviewer) {
                    throw new Error(`Slot ${index + 1}: Interviewer is required`);
                }

                // Additional validation
                const date = new Date(slot.slotDateTime);
                if (isNaN(date.getTime())) {
                    throw new Error(`Slot ${index + 1}: Invalid date format - ${slot.slotDateTime}`);
                }
            });

            const requestData = { slots: slotsData };

            const response = await api.post('/slots/bulk', requestData, {
                timeout: 30000
            });

            return response.data.summary;
        } catch (error) {
            if (error.response) {
                const serverError = error.response.data?.error || 'Server error occurred';
                throw new Error(serverError);
            } else if (error.code === 'ECONNABORTED') {
                throw new Error('Request timeout. The bulk operation may still be processing.');
            } else {
                throw new Error(error.message || 'Failed to create bulk slots');
            }
        }
    },

    bookSlot: async (slotId, token) => {
        try {
            // Validate input parameters
            if (!slotId) {
                throw new Error('Slot ID is required');
            }
            
            if (!token) {
                throw new Error('Authentication token is required');
            }

            // Ensure slotId is a number and token is a clean string
            const bookingData = {
                slotId: parseInt(slotId), 
                token: token.trim()
            };

            // Validate slotId is a valid number
            if (isNaN(bookingData.slotId)) {
                throw new Error('Invalid slot ID format');
            }

            const response = await api.post('/slots/book', bookingData);
            return response.data;
        } catch (error) {
            // Enhanced error handling with specific messages
            if (error.response) {
                const serverError = error.response.data?.error || 'Server error occurred';
                const statusCode = error.response.status;
                
                // Handle specific status codes
                switch (statusCode) {
                    case 400:
                        throw new Error(serverError);
                    case 401:
                        throw new Error('Authentication failed. Please check your invitation link.');
                    case 404:
                        throw new Error('Slot not found or invitation token is invalid.');
                    case 409:
                        throw new Error('Slot is no longer available. Please select another slot.');
                    case 500:
                        throw new Error('Server error. Please try again later.');
                    default:
                        throw new Error(serverError);
                }
            } else if (error.request) {
                if (error.code === 'ECONNABORTED') {
                    throw new Error('Request timeout. Please check your connection and try again.');
                }
                throw new Error('Network error. Please check your internet connection.');
            } else {
                throw new Error(error.message || 'An unexpected error occurred while booking the slot.');
            }
        }
    },

    deleteSlot: async (id) => {
        try {
            if (!id) {
                throw new Error('Slot ID is required');
            }

            const slotId = parseInt(id);
            if (isNaN(slotId)) {
                throw new Error('Invalid slot ID format');
            }

            const response = await api.delete(`/slots/${slotId}`);
            return response.data;
        } catch (error) {
            if (error.response?.status === 404) {
                throw new Error('Slot not found');
            } else if (error.response?.status === 400) {
                throw new Error(error.response.data?.error || 'Cannot delete this slot');
            }
            
            throw error;
        }
    },

    // Bulk delete slots
    deleteBulkSlots: async (slotIds) => {
        try {
            if (!Array.isArray(slotIds) || slotIds.length === 0) {
                throw new Error('Slot IDs array is required');
            }

            const response = await api.delete('/slots/bulk', {
                data: { slotIds }
            });

            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Cancel booking
    cancelBooking: async (slotId, reason = 'Cancelled by admin') => {
        try {
            if (!slotId) {
                throw new Error('Slot ID is required');
            }

            const response = await api.delete(`/slots/${slotId}/cancel`, {
                data: { reason }
            });

            return response.data;
        } catch (error) {
            if (error.response?.status === 404) {
                throw new Error('Booking not found');
            } else if (error.response?.status === 400) {
                throw new Error(error.response.data?.error || 'Cannot cancel this booking');
            }
            
            throw error;
        }
    },

    // Helper method to validate slot data
    validateSlotData: (slotData) => {
        if (!slotData) {
            throw new Error('Slot data is required');
        }

        if (!slotData.slotDateTime) {
            throw new Error('Slot date and time are required');
        }

        const slotDate = new Date(slotData.slotDateTime);
        const now = new Date();

        if (isNaN(slotDate.getTime())) {
            throw new Error('Invalid date format');
        }

        if (slotDate <= now) {
            throw new Error('Slot date must be in the future');
        }

        return true;
    },

    // Validate bulk slot data
    validateBulkSlotData: (slotsData) => {
        if (!Array.isArray(slotsData) || slotsData.length === 0) {
            throw new Error('Slots data array is required');
        }

        if (slotsData.length > 500) {
            throw new Error('Cannot create more than 500 slots at once');
        }

        const errors = [];
        slotsData.forEach((slot, index) => {
            try {
                slotService.validateSlotData(slot);
                
                if (!slot.interviewer) {
                    errors.push(`Slot ${index + 1}: Interviewer is required`);
                }
            } catch (error) {
                errors.push(`Slot ${index + 1}: ${error.message}`);
            }
        });

        if (errors.length > 0) {
            throw new Error('Validation errors:\n' + errors.join('\n'));
        }

        return true;
    },

    // Helper method to format slot date for display
    formatSlotDateTime: (dateTimeString) => {
        try {
            const date = new Date(dateTimeString);
            return {
                date: date.toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                }),
                time: date.toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                }),
                full: date.toLocaleString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                }),
                isToday: date.toDateString() === new Date().toDateString(),
                isTomorrow: date.toDateString() === new Date(Date.now() + 86400000).toDateString(),
                dayOfWeek: date.toLocaleDateString('en-US', { weekday: 'short' }),
                shortDate: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                shortTime: date.toLocaleTimeString('en-US', { 
                    hour: 'numeric', 
                    minute: '2-digit',
                    hour12: true 
                })
            };
        } catch (error) {
            return {
                date: 'Invalid Date',
                time: 'Invalid Time',
                full: 'Invalid Date/Time',
                isToday: false,
                isTomorrow: false,
                dayOfWeek: '---',
                shortDate: '---',
                shortTime: '---'
            };
        }
    },

    // Helper method to check if a slot is bookable
    isSlotBookable: (slotDateTime) => {
        const slotDate = new Date(slotDateTime);
        const now = new Date();
        const bufferMinutes = 30;
        
        return slotDate > new Date(now.getTime() + (bufferMinutes * 60 * 1000));
    },

    // Generate slot times based on duration
    generateSlotTimes: (startTime, endTime, durationMinutes) => {
        try {
            const slots = [];
            
            const [startHour, startMinute] = startTime.split(':').map(Number);
            const [endHour, endMinute] = endTime.split(':').map(Number);
            
            let currentTime = new Date();
            currentTime.setHours(startHour, startMinute, 0, 0);
            
            const endDateTime = new Date();
            endDateTime.setHours(endHour, endMinute, 0, 0);
            
            while (currentTime < endDateTime) {
                const nextSlot = new Date(currentTime.getTime() + (durationMinutes * 60000));
                if (nextSlot <= endDateTime) {
                    slots.push({
                        time: currentTime.toTimeString().slice(0, 5),
                        display: currentTime.toLocaleTimeString('en-US', { 
                            hour: 'numeric', 
                            minute: '2-digit',
                            hour12: true 
                        }),
                        value: currentTime.toTimeString()
                    });
                }
                currentTime.setTime(currentTime.getTime() + (durationMinutes * 60000));
            }
            
            return slots;
        } catch (error) {
            return [];
        }
    },

    // Calculate total slots for date range
    calculateTotalSlots: (startDate, endDate, startTime, endTime, duration, skipWeekends = true) => {
        try {
            let totalSlots = 0;
            const currentDate = new Date(startDate);
            const finalDate = new Date(endDate);

            while (currentDate <= finalDate) {
                if (skipWeekends && (currentDate.getDay() === 0 || currentDate.getDay() === 6)) {
                    currentDate.setDate(currentDate.getDate() + 1);
                    continue;
                }

                const daySlots = slotService.generateSlotTimes(startTime, endTime, duration);
                totalSlots += daySlots.length;

                currentDate.setDate(currentDate.getDate() + 1);
            }

            return totalSlots;
        } catch (error) {
            return 0;
        }
    },

    // Method to get slot statistics
    getSlotStats: async () => {
        try {
            const [allSlots, availableSlots] = await Promise.all([
                slotService.getSlots(),
                slotService.getAvailableSlots()
            ]);

            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const tomorrow = new Date(today.getTime() + 86400000);

            const stats = {
                total: allSlots.length,
                available: availableSlots.length,
                booked: allSlots.filter(slot => slot.IsBooked).length,
                today: availableSlots.filter(slot => {
                    const slotDate = new Date(slot.SlotDateTime);
                    return slotDate.toDateString() === today.toDateString();
                }).length,
                tomorrow: availableSlots.filter(slot => {
                    const slotDate = new Date(slot.SlotDateTime);
                    return slotDate.toDateString() === tomorrow.toDateString();
                }).length,
                upcoming: availableSlots.filter(slot => {
                    const slotDate = new Date(slot.SlotDateTime);
                    return slotDate > tomorrow;
                }).length,
                thisWeek: availableSlots.filter(slot => {
                    const slotDate = new Date(slot.SlotDateTime);
                    const weekFromNow = new Date(now.getTime() + (7 * 24 * 60 * 60 * 1000));
                    return slotDate <= weekFromNow;
                }).length,
                byInterviewer: allSlots.reduce((acc, slot) => {
                    const interviewer = slot.Interviewer || 'Unassigned';
                    acc[interviewer] = (acc[interviewer] || 0) + 1;
                    return acc;
                }, {})
            };

            return stats;
        } catch (error) {
            throw error;
        }
    },

    // Get slots by date range
    getSlotsByDateRange: async (startDate, endDate) => {
        try {
            const response = await api.get('/slots/range', {
                params: { startDate, endDate }
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Get slots by interviewer
    getSlotsByInterviewer: async (interviewer) => {
        try {
            const response = await api.get('/slots/interviewer', {
                params: { interviewer }
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }
};

export default slotService;
